/**
 * Event landing-page template — summit ("Executive Summit").
 *
 * P9-W5: SECOND premium 2026 template. Distinct CraftJS home-page document
 * for the `summit` theme — editorial light register for conferences and
 * executive business events: warm paper surface, near-black ink, ONE deep-teal
 * accent, oversized Fraunces display serif over Hanken Grotesk body, kicker
 * eyebrows, hairline rules and a numbered conference programme as the heart
 * of the page.
 *
 * The editorial surface + motion live in `summitPreset.customCss`
 * (self-hosted @font-face, @keyframes fade-up/rule-grow, scroll-reveal via
 * `animation-timeline: view()`, everything motion-gated behind
 * `prefers-reduced-motion: no-preference`). Motion is deliberately quieter
 * than networkingNight — one entrance, one slow rule-expand, no loops on
 * content. Section DOM ids = node ids, so the css targets `#smt_*` directly.
 *
 * Convention (mirrors networkingNight/gala siblings):
 *  - Section-level marketing copy goes through td() keys (`smt*` prefix).
 *    Hero copy keys end in `HeroHeader` / `HeroEyebrow` (P9-W2 fact hooks).
 *  - Repetitive structured child data (agenda rows, numbers, FAQ, speakers)
 *    also goes through td() keys (Wave 3b) — only untranslatable bits
 *    (times, bare numbers, fictitious person names) stay inline.
 *  - Hero primary CTA anchors "#"+DataInput node id, so OPEN registration's
 *    strip + dead-anchor fixup applies cleanly.
 */
import { defaultRegistrationConsents, toCheckboxItems } from "@/lib/website/registrationConsents";
import {
  A,
  enc,
  pv,
  tc,
  btn,
  fi,
  acceptanceFi,
  scaffold,
  emptyFieldsGroup,
  PAD,
  DIST_CARD,
  BTN_FORM_SP,
  SEC_MARGIN,
  ACCEPTANCE_VISIBILITY_FILTER,
  type N,
  type TranslationFn,
} from "../temp";
import { EASE, decorLayer, fadeUpKeyframes, fontFaces, heroShell, motionGate, revealBlock } from "./premium-css";
import type { WebsiteThemeStylePreset } from "@/types/WebsiteTheme";
import {
  SAMPLE_SPEAKER_IMAGES,
} from "@/lib/website-builder/sampleContent";


// Editorial paper palette (calibre-reference discipline: ink, paper, ONE accent)
const PAPER = "#FAF7F2"; // warm paper page background
const PAPER_DEEP = "#F2EDE3"; // slightly sunk paper (alternating sections)
const INK = "#161412"; // near-black ink headings / rules
const TEAL = "#0F5E59"; // the one restrained accent (deep teal)
const TEAL_DEEP = "#0B4742"; // hover / hyperlink-hover
const MUTE = "#5D5850"; // muted body
const HAIR = "#E4DDD0"; // hairline rules / countdown borders
const DISPLAY = "'Fraunces', Georgia, 'Times New Roman', serif";
const BODY_FONT = "'Hanken Grotesk', 'Helvetica Neue', sans-serif";

const LOGO = `${A}/shared/logo.svg`;

// ── Structured child data (Wave 3b: td() keys; times / bare numbers /
//    fictitious person names deliberately stay inline) ─────────────────

const numbersData = (td: TranslationFn) => [
  { num: "<b>240</b>", desc: td("smtNum1Desc") },
  { num: "<b>12</b>", desc: td("smtNum2Desc") },
  { num: "<b>18</b>", desc: td("smtNum3Desc") },
  { num: "<b>190</b>", desc: td("smtNum4Desc") },
];

const agendaData = (td: TranslationFn) => [
  { time: "<b>01 · 08:30</b>", title: td("smtAgendaRow1Title"), who: td("smtAgendaRow1Who"), desc: td("smtAgendaRow1Desc") },
  { time: "<b>02 · 09:15</b>", title: td("smtAgendaRow2Title"), who: td("smtAgendaRow2Who"), desc: td("smtAgendaRow2Desc") },
  { time: "<b>03 · 10:30</b>", title: td("smtAgendaRow3Title"), who: td("smtAgendaRow3Who"), desc: td("smtAgendaRow3Desc") },
  { time: "<b>04 · 12:30</b>", title: td("smtAgendaRow4Title"), who: td("smtAgendaRow4Who"), desc: td("smtAgendaRow4Desc") },
  { time: "<b>05 · 14:00</b>", title: td("smtAgendaRow5Title"), who: td("smtAgendaRow5Who"), desc: td("smtAgendaRow5Desc") },
  { time: "<b>06 · 16:30</b>", title: td("smtAgendaRow6Title"), who: td("smtAgendaRow6Who"), desc: td("smtAgendaRow6Desc") },
];

const speakerData = (td: TranslationFn) => [
  { name: "Dr. Vera Sandmann", role: td("smtSpeaker1Role") },
  { name: "Tobias Brandt", role: td("smtSpeaker2Role") },
  { name: "Prof. Lena Okafor", role: td("smtSpeaker3Role") },
];

const faqData = (td: TranslationFn) => [
  { q: `<b>${td("smtFaq1Question")}</b>`, a: td("smtFaq1Answer") },
  { q: `<b>${td("smtFaq2Question")}</b>`, a: td("smtFaq2Answer") },
  { q: `<b>${td("smtFaq3Question")}</b>`, a: td("smtFaq3Answer") },
  { q: `<b>${td("smtFaq4Question")}</b>`, a: td("smtFaq4Answer") },
  { q: `<b>${td("smtFaq5Question")}</b>`, a: td("smtFaq5Answer") },
];

// ── summit-specific child factories ───────────────────────────────────

function numbersChild(parent: string, i: number, td: TranslationFn): N {
  const d = numbersData(td)[i];
  return {
    type: { resolvedName: "NumbersChild" }, isCanvas: false,
    props: {
      isVisible: true,
      isVisibleNumber: true, textNumber: d.num, fontSizeNumber: "text-4xl", textAlignNumber: "center",
      isVisibleDescription: true, textDescription: d.desc, fontSizeDescription: "text-sm", textAlignDescription: "center",
    },
    displayName: "NumbersChild", custom: { icon: "IconNumber" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function agendaChild(parent: string, i: number, td: TranslationFn): N {
  const d = agendaData(td)[i];
  return {
    type: { resolvedName: "AgendaChild" }, isCanvas: false,
    props: {
      isVisibleText1: true, textText1: d.time, fontSizeText1: "text-sm", textAlignText1: "left",
      isVisibleText2: true, textText2: d.title, fontSizeText2: "text-sm", textAlignText2: "left",
      isVisibleText3: true, textText3: d.who, fontSizeText3: "text-sm", textAlignText3: "left",
      isVisibleText: true, textText: d.desc, fontSizeText: "text-sm", textAlignText: "left", border: true,
    },
    displayName: "AgendaChild", custom: { icon: "IconCalendar" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function speakerChild(parent: string, i: number, td: TranslationFn): N {
  const d = speakerData(td)[i];
  return {
    type: { resolvedName: "SpeakerChild" }, isCanvas: false,
    props: {
      isVisible: true, imageUrl: SAMPLE_SPEAKER_IMAGES[i % SAMPLE_SPEAKER_IMAGES.length], imageBottomBackground: "transparent",
      isVisibleName: true, textName: d.name, fontSizeName: "text-lg", textAlignName: "left",
      isVisibleOccupation: true, textOccupation: d.role, fontSizeOccupation: "text-sm", textAlignOccupation: "left",
      icons: [{ icon: "IconBrandLinkedin" }, { icon: "IconMail" }], isVisibleIcon: true,
    },
    displayName: "SpeakerChild", custom: { icon: "IconMicrophone" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function faqChild(parent: string, i: number, td: TranslationFn): N {
  const d = faqData(td)[i];
  return {
    type: { resolvedName: "FaQChild" }, isCanvas: false,
    props: {
      isVisibleQuestion: true, textQuestion: d.q, fontSizeQuestion: "text-sm", textAlignQuestion: "left",
      isVisibleAnswer: true, textAnswer: d.a, fontSizeAnswer: "text-sm", textAlignAnswer: "left", border: true,
    },
    displayName: "FaQChild", custom: { icon: "IconMessageQuestion" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

// ── Theme CSS (P9-W5) ─────────────────────────────────────────────────
//
// Marker comment first so a later user edit is distinguishable from the
// pristine preset. Composed from the shared premium-css builders (WS3) —
// output pinned byte-for-byte in __tests__/premiumCss.test.ts. Business
// register: ONE entrance fade, ONE slow rule-expand, scroll-reveal — nothing
// loops.
const CUSTOM_CSS = `/* theme:summit */
${fontFaces([
  { family: "Fraunces", file: "fraunces" },
  { family: "Hanken Grotesk", file: "hanken-grotesk" },
])}

/* Editorial hero — warm paper sheet, faint teal wash, hairline base rule */
#smt_hero {
${heroShell}
  background-image:
    linear-gradient(to bottom, rgba(15, 94, 89, 0.05) 0%, rgba(15, 94, 89, 0) 34%),
    linear-gradient(180deg, #FFFDF8 0%, ${PAPER} 58%, ${PAPER_DEEP} 100%);
  border-bottom: 1px solid ${HAIR};
}

/* Masthead rule: a thin teal bar across the top of the sheet (static) */
#smt_hero::before {
${decorLayer}
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: ${TEAL};
}

/* Editorial underline: an ink hairline that grows in under the masthead */
#smt_hero::after {
${decorLayer}
  left: 6%;
  right: 6%;
  bottom: 10%;
  height: 1px;
  background: ${INK};
  opacity: 0.28;
}

${motionGate(`${fadeUpKeyframes("smt", 16)}
  @keyframes smtRuleGrow {
    from { transform: scaleX(0); }
    to { transform: scaleX(1); }
  }

  /* Quiet entrance: the sheet settles, then the hairline draws itself */
  #smt_hero { animation: smtFadeUp 0.7s ${EASE} both; }
  #smt_hero::after {
    transform-origin: left center;
    animation: smtRuleGrow 1.4s ${EASE} 0.4s both;
  }

${revealBlock("smt", ["numbers", "agenda", "speaker", "datainput", "text", "countdown", "faq", "map", "contact"], 30)}`)}
`;

// ── Style preset ──────────────────────────────────────────────────────

export const summitPreset: WebsiteThemeStylePreset = {
  // The hero is a CSS editorial sheet (no photo); heroImage just needs a valid key.
  heroImage: `${A}/shared/hero.jpg`,
  logoSrc: LOGO,
  colorPalette: { primary: INK, secondary: TEAL, background: PAPER },
  fonts: { header: DISPLAY, body: BODY_FONT },
  rounding: "rounded-none",
  recommendedSize: "large",
  bodyTheme: { fontColor: "#46423B", fontSize: "16px", layoutOption: "full-width" },
  headingsTheme: {
    h1: { fontFamily: DISPLAY, fontSize: "40px", fontColor: INK },
    h2: { fontFamily: DISPLAY, fontSize: "24px", fontColor: INK },
  },
  buttonsTheme: {
    primary: { bgColor: INK, bgHoverColor: TEAL_DEEP, borderColor: INK, borderHoverColor: TEAL_DEEP, fontColor: "#FFFFFF", fontHoverColor: "#FFFFFF", fontSize: "16px", fontHoverSize: "16px", borderRadius: "rounded-none", hoverBorderRadius: "rounded-none" },
    secondary: { bgColor: "transparent", bgHoverColor: TEAL, borderColor: TEAL, borderHoverColor: TEAL, fontColor: TEAL, fontHoverColor: "#FFFFFF", fontSize: "16px", fontHoverSize: "16px", borderRadius: "rounded-none", hoverBorderRadius: "rounded-none" },
  },
  hyperlinkTheme: { fontColor: TEAL, fontHoverColor: TEAL_DEEP, underline: true },
  formInputTheme: { labelFontColor: INK, inputFontColor: INK, backgroundColor: "#FFFFFF", border: true, borderColor: INK, borderRadius: "rounded-none", borderStyle: "border-solid", borderWidth: "border", borderSides: [], fontFamily: BODY_FONT, fontSize: "text-sm", errorColor: "#B3261E" },
  customCss: CUSTOM_CSS,
};

// ── Template ──────────────────────────────────────────────────────────

export const summit = (td: TranslationFn): string => {
  const B = "smt_body";

  const HERO = "smt_hero";
  const NUMBERS = "smt_numbers";
  const AGENDA = "smt_agenda";
  const SPEAKER = "smt_speaker";
  const DI = "smt_datainput";
  const TEXT = "smt_text";
  const COUNTDOWN = "smt_countdown";
  const FAQ = "smt_faq";
  const MAP = "smt_map";
  const CONTACT = "smt_contact";

  return enc({
    ...scaffold(
      { ab: "smt_ab", abs: "smt_abs", body: B, ft: "smt_ft", fts: "smt_fts" },
      [HERO, NUMBERS, AGENDA, SPEAKER, DI, TEXT, COUNTDOWN, FAQ, MAP, CONTACT],
      false, { background: "bg-white", padding: "p-4", borderRadius: "rounded-none" },
    ),

    // ── Hero (paper editorial sheet — no photo, oversized serif headline) ──
    [HERO]: {
      type: { resolvedName: "Hero" }, isCanvas: false,
      props: {
        sectionHeight: 92, contentAlignment: "center", imageLayout: "background", layoutWidth: "33%", inversed: false,
        padding: PAD, distButtons: "gap-3 wb-md:gap-4", distContentImage: "gap-6 wb-md:gap-10 wb-lg:gap-16", distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", buttonAlignment: "flex-row", isVisible: true,
        isVisibleHeadline: true, textHeadline: td("smtHeroEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left", headlineColor: TEAL,
        isVisibleHeader: true, textHeader: td("smtHeroHeader"), fontSizeHeader: "text-6xl", textAlignHeader: "left", headerColor: INK,
        isVisibleText: true, textText: td("smtHeroText"), fontSizeText: "text-lg", textAlignText: "left", textColor: MUTE,
        secondaryButtonSize: "medium", secondaryButtonText: td("smtHeroSecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false,
        primaryButtonSize: "medium", primaryButtonText: td("smtHeroPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false,
        // Paper surface comes from customCss (#smt_hero gradient); the image
        // is OFF so the Hero falls back to backgroundColor under the css.
        backgroundType: "image", isVisibleImage: false, imageRounded: false, backgroundOverlayColor: "transparent",
        imageSrc: `${A}/shared/hero.jpg`, backgroundColor: PAPER,
      },
      displayName: "Hero", custom: { icon: "IconSlideshow", preview: pv("Hero-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "smt_hero_eb", header: "smt_hero_hd", text: "smt_hero_tx", buttonOne: "smt_hero_b1", buttonTwo: "smt_hero_b2" },
    },
    smt_hero_eb: tc(HERO, { isVisible: true, text: td("smtHeroEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: TEAL, header: true }),
    smt_hero_hd: tc(HERO, { isVisible: true, text: td("smtHeroHeader"), fontSize: "text-6xl", textAlign: "left", displayName: "header", color: INK, header: true }),
    smt_hero_tx: tc(HERO, { isVisible: true, text: td("smtHeroText"), fontSize: "text-lg", textAlign: "left", displayName: "text", color: MUTE, header: false }),
    smt_hero_b1: btn(HERO, { type: "secondary", linkType: "internal", linkUrl: "#" + AGENDA, text: td("smtHeroSecondaryButton"), displayName: "buttonOne" }),
    smt_hero_b2: btn(HERO, { type: "primary", linkType: "internal", linkUrl: "#" + DI, text: td("smtHeroPrimaryButton"), displayName: "buttonTwo" }),

    // ── Numbers (honest conference facts: seats / sessions / speakers / breaks) ──
    [NUMBERS]: {
      type: { resolvedName: "Numbers" }, isCanvas: true,
      props: {
        sectionHeight: 60, contentAlignment: "center", sectionMargin: SEC_MARGIN, gridColumns: "4",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "gap-5 wb-md:gap-6", isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("smtNumbersHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("smtNumbersHeader"), fontSizeHeader: "text-2xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("smtNumbersText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Numbers", custom: { icon: "Icon123", preview: pv("NumbersPreview"), allowedComponents: ["NumbersChild"] },
      parent: B, hidden: false, nodes: ["smt_num1", "smt_num2", "smt_num3", "smt_num4"],
      linkedNodes: { headline: "smt_num_eb", header: "smt_num_hd", text: "smt_num_tx" },
    },
    smt_num_eb: tc(NUMBERS, { isVisible: false, text: td("smtNumbersHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    smt_num_hd: tc(NUMBERS, { isVisible: true, text: td("smtNumbersHeader"), fontSize: "text-2xl", textAlign: "center", displayName: "header", header: true }),
    smt_num_tx: tc(NUMBERS, { isVisible: true, text: td("smtNumbersText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    smt_num1: numbersChild(NUMBERS, 0, td),
    smt_num2: numbersChild(NUMBERS, 1, td),
    smt_num3: numbersChild(NUMBERS, 2, td),
    smt_num4: numbersChild(NUMBERS, 3, td),

    // ── Agenda (the heart — numbered conference programme, 08:30 → 16:30) ──
    [AGENDA]: {
      type: { resolvedName: "Agenda" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "space-y-3 wb-md:space-y-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("smtAgendaHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("smtAgendaHeader"), fontSizeHeader: "text-2xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("smtAgendaText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Agenda", custom: { icon: "IconListCheck", preview: pv("AgendaPreview"), allowedComponents: ["AgendaChild"] },
      parent: B, hidden: false, nodes: ["smt_ag1", "smt_ag2", "smt_ag3", "smt_ag4", "smt_ag5", "smt_ag6"],
      linkedNodes: { headline: "smt_ag_eb", header: "smt_ag_hd", text: "smt_ag_tx" },
    },
    smt_ag_eb: tc(AGENDA, { isVisible: false, text: td("smtAgendaHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    smt_ag_hd: tc(AGENDA, { isVisible: true, text: td("smtAgendaHeader"), fontSize: "text-2xl", textAlign: "center", displayName: "header", header: true }),
    smt_ag_tx: tc(AGENDA, { isVisible: true, text: td("smtAgendaText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    smt_ag1: agendaChild(AGENDA, 0, td),
    smt_ag2: agendaChild(AGENDA, 1, td),
    smt_ag3: agendaChild(AGENDA, 2, td),
    smt_ag4: agendaChild(AGENDA, 3, td),
    smt_ag5: agendaChild(AGENDA, 4, td),
    smt_ag6: agendaChild(AGENDA, 5, td),

    // ── Speakers (VISIBLE — summits sell speakers; seeded 10-corporate keys) ──
    [SPEAKER]: {
      type: { resolvedName: "Speaker" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextSpeakers: "gap-8 wb-md:gap-12", distSpeakers: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: true, gridColumns: "3", layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("smtSpeakerHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("smtSpeakerHeader"), fontSizeHeader: "text-2xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("smtSpeakerText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Speaker", custom: { icon: "IconUserStar", preview: pv("Speaker-preview"), allowedComponents: ["SpeakerChild"] },
      parent: B, hidden: false, nodes: ["smt_sp1", "smt_sp2", "smt_sp3"],
      linkedNodes: { headline: "smt_sp_eb", header: "smt_sp_hd", text: "smt_sp_tx" },
    },
    smt_sp_eb: tc(SPEAKER, { isVisible: false, text: td("smtSpeakerHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    smt_sp_hd: tc(SPEAKER, { isVisible: true, text: td("smtSpeakerHeader"), fontSize: "text-2xl", textAlign: "center", displayName: "header", header: true }),
    smt_sp_tx: tc(SPEAKER, { isVisible: true, text: td("smtSpeakerText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    smt_sp1: speakerChild(SPEAKER, 0, td),
    smt_sp2: speakerChild(SPEAKER, 1, td),
    smt_sp3: speakerChild(SPEAKER, 2, td),

    // ── DataInput (seat request — single step, company + role) ──
    [DI]: {
      type: { resolvedName: "DataInput" }, isCanvas: false,
      props: {
        padding: PAD, sectionMargin: SEC_MARGIN, isVisible: true, textSpacing: DIST_CARD,
        backgroundColor: "transparent", border: false, borderWidth: "border-2", borderStyle: "border-solid",
        borderSides: ["bottom"], borderRadius: undefined,
        isVisibleHeadline: true, textHeadline: td("smtDataHeadline"), fontSizeHeadline: "text-base", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("smtDataHeader"), fontSizeHeader: "text-lg", textAlignHeader: "center",
        isVisibleText: true, textText: td("smtDataText"), fontSizeText: "text-sm", textAlignText: "center",
        allowUpdate: true,
        multiStepEnabled: false, stepLabels: [], multiStepMode: "pages",
        overviewEnabled: false, stepSections: [["fields-0", "companions"]],
      },
      displayName: "DataInput", custom: { icon: "IconForms", preview: pv("DataInput-Preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: {
        headline: "smt_di_hl", header: "smt_di_hd", text: "smt_di_tx",
        fieldsGroup0: "smt_di_f0", fieldsGroup1: "smt_di_f1", fieldsGroup2: "smt_di_f2",
        fieldsGroup3: "smt_di_f3", fieldsGroup4: "smt_di_f4",
        companionsGroup: "smt_di_comp", overviewGroup: "smt_di_ov", checkboxes: "smt_di_cb",
        button: "smt_di_btn", backButton: "smt_di_back", nextButton: "smt_di_next",
      },
    },
    smt_di_hl: tc(DI, { isVisible: true, text: td("smtDataHeadline"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-base", textAlign: "center", displayName: "headline", header: true }),
    smt_di_hd: tc(DI, { isVisible: true, text: td("smtDataHeader"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-lg", textAlign: "center", displayName: "header", header: true }),
    smt_di_tx: tc(DI, { isVisible: true, text: td("smtDataText"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-sm", textAlign: "center", displayName: "text", header: false }),
    smt_di_f0: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 6, groupIndex: 0, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["smt_di_acceptance", "smt_di_first", "smt_di_last", "smt_di_email", "smt_di_company", "smt_di_role"], linkedNodes: {},
    },
    // FIRST in the group, and filter-free — the question every other
    // field in this group is gated on. See `acceptanceFi`.
    smt_di_acceptance: acceptanceFi("smt_di_f0", td("acceptanceState")),
    smt_di_first: fi("smt_di_f0", { label: td("firstName"), requiredField: true, inputFieldVariable: "firstName", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    smt_di_last: fi("smt_di_f0", { label: td("lastName"), requiredField: true, inputFieldVariable: "lastName", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    smt_di_email: fi("smt_di_f0", { label: td("email"), requiredField: true, inputFieldVariable: "email", gridArea: "col-span-12", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    smt_di_company: fi("smt_di_f0", { label: td("smtFieldCompany"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "company", changeable: true }),
    smt_di_role: fi("smt_di_f0", { label: td("smtFieldRole"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "role", changeable: true }),
    smt_di_f1: emptyFieldsGroup(DI, 1),
    smt_di_f2: emptyFieldsGroup(DI, 2),
    smt_di_f3: emptyFieldsGroup(DI, 3),
    smt_di_f4: emptyFieldsGroup(DI, 4),
    smt_di_comp: {
      type: { resolvedName: "CompanionsGroup" }, isCanvas: true,
      props: { isVisible: false, fieldCount: 1, fieldSpacing: "medium", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER },
      displayName: "CompanionsGroup", custom: { icon: "IconUsers", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["smt_di_comp_first"], linkedNodes: {},
    },
    smt_di_comp_first: fi("smt_di_comp", { label: td("firstName"), requiredField: false, inputFieldVariable: "firstName", gridArea: "col-span-12", changeable: true, type: "companion" }),
    smt_di_ov_t: tc("smt_di_ov", { isVisible: true, text: td("overviewTitle"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-base", textAlign: "left", displayName: "overviewTitle", header: true }),
    smt_di_ov_x: tc("smt_di_ov", { isVisible: true, text: td("overviewText"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-sm", textAlign: "left", displayName: "overviewText", header: false }),
    smt_di_ov: {
      type: { resolvedName: "OverviewGroup" }, isCanvas: true,
      props: { isVisible: false, parentNodeId: DI, stepSections: [["fields-0", "companions"]], stepLabels: [] },
      displayName: "summaryGroup", custom: { icon: "IconListDetails" },
      parent: DI, hidden: false, nodes: ["smt_di_ov_t", "smt_di_ov_x"], linkedNodes: {},
    },
    smt_di_cb: {
      type: { resolvedName: "Checkboxes" }, isCanvas: false,
      props: { isVisible: true, checkboxes: toCheckboxItems(defaultRegistrationConsents(td)), displayName: "checkboxes" },
      displayName: "Checkboxes", custom: { icon: "IconCheckbox" },
      parent: DI, hidden: false, nodes: [], linkedNodes: {},
    },
    smt_di_btn: btn(DI, { linkType: "internal", linkUrl: "", text: td("smtDataSubmit"), submittedText: "", isSubmitted: false, enableSubmittedState: true, size: "small", widthMode: "full", buttonType: "submit", margin: BTN_FORM_SP, displayName: "button" }),
    smt_di_back: btn(DI, { linkType: "internal", type: "secondary", linkUrl: "", text: td("back"), navigationRole: "back", size: "small", widthMode: "content", isVisible: false, displayName: "backButton" }),
    smt_di_next: btn(DI, { linkType: "internal", linkUrl: "", text: td("next"), navigationRole: "next", size: "small", widthMode: "content", isVisible: false, displayName: "nextButton" }),

    // ── Text (the programme director's editorial note) ──
    [TEXT]: {
      type: { resolvedName: "TextSection" }, isCanvas: false,
      props: {
        buttonAlign: "center", padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", distButtons: "gap-3 wb-md:gap-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("smtTextHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("smtTextHeader"), fontSizeHeader: "text-2xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("smtTextBody"), fontSizeText: "text-md", textAlignText: "center",
        secondaryButtonSize: "medium", secondaryButtonText: td("smtTextSecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false, secondaryButtonAlign: "center",
        primaryButtonSize: "medium", primaryButtonText: td("smtTextPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false, primaryButtonAlign: "center",
      },
      displayName: "Text", custom: { icon: "IconAlignJustified", preview: pv("textPreview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "smt_tx_eb", header: "smt_tx_hd", text: "smt_tx_tx", buttonOne: "smt_tx_b1", buttonTwo: "smt_tx_b2" },
    },
    smt_tx_eb: tc(TEXT, { isVisible: false, text: td("smtTextHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    smt_tx_hd: tc(TEXT, { isVisible: true, text: td("smtTextHeader"), fontSize: "text-2xl", textAlign: "center", displayName: "header", header: true }),
    smt_tx_tx: tc(TEXT, { isVisible: true, text: td("smtTextBody"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    smt_tx_b1: btn(TEXT, { type: "secondary", linkType: "internal", linkUrl: "#" + CONTACT, text: td("smtTextSecondaryButton"), displayName: "buttonOne" }),
    smt_tx_b2: btn(TEXT, { type: "primary", linkType: "internal", linkUrl: "#" + DI, text: td("smtTextPrimaryButton"), displayName: "buttonTwo" }),

    // ── Countdown ──
    [COUNTDOWN]: {
      type: { resolvedName: "Countdown" }, isCanvas: false,
      props: {
        sectionHeight: 40, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distButtonCountdown: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: true, buttonAlign: "left",
        isVisibleHeadline: true, textHeadline: td("smtCountdownEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("smtCountdownHeader"), fontSizeHeader: "text-4xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("smtCountdownText"), fontSizeText: "text-base", textAlignText: "left",
        buttonSize: "medium", buttonText: td("smtCountdownButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false,
        isVisiblCountdown: true, countdownSizeDate: "text-5xl", countdownFontTypeDate: DISPLAY, countdownFontColorDate: INK,
        countdownSize: "text-base", countdownFontColor: MUTE, countdownBorderColor: HAIR,
      },
      displayName: "Countdown", custom: { icon: "IconAlarm", preview: pv("Countdown-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "smt_cd_eb", header: "smt_cd_hd", text: "smt_cd_tx", button: "smt_cd_btn" },
    },
    smt_cd_eb: tc(COUNTDOWN, { isVisible: true, text: td("smtCountdownEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: TEAL, header: true }),
    smt_cd_hd: tc(COUNTDOWN, { isVisible: true, text: td("smtCountdownHeader"), fontSize: "text-4xl", textAlign: "left", displayName: "header", color: INK, header: true }),
    smt_cd_tx: tc(COUNTDOWN, { isVisible: true, text: td("smtCountdownText"), fontSize: "text-base", textAlign: "left", displayName: "text", color: MUTE, header: false }),
    smt_cd_btn: btn(COUNTDOWN, { linkType: "internal", linkUrl: "#" + DI, text: td("smtCountdownButton"), displayName: "ButtonChild" }),

    // ── FaQ ──
    [FAQ]: {
      type: { resolvedName: "FaQ" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "space-y-3 wb-md:space-y-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("smtFaqHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("smtFaqHeader"), fontSizeHeader: "text-2xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("smtFaqText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "FaQ", custom: { icon: "IconQuestionMark", preview: pv("faqPreview"), allowedComponents: ["FaQChild"] },
      parent: B, hidden: false, nodes: ["smt_fq1", "smt_fq2", "smt_fq3", "smt_fq4", "smt_fq5"],
      linkedNodes: { headline: "smt_fq_eb", header: "smt_fq_hd", text: "smt_fq_tx" },
    },
    smt_fq_eb: tc(FAQ, { isVisible: false, text: td("smtFaqHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    smt_fq_hd: tc(FAQ, { isVisible: true, text: td("smtFaqHeader"), fontSize: "text-2xl", textAlign: "center", displayName: "header", header: true }),
    smt_fq_tx: tc(FAQ, { isVisible: true, text: td("smtFaqText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    smt_fq1: faqChild(FAQ, 0, td),
    smt_fq2: faqChild(FAQ, 1, td),
    smt_fq3: faqChild(FAQ, 2, td),
    smt_fq4: faqChild(FAQ, 3, td),
    smt_fq5: faqChild(FAQ, 4, td),

    // ── Map (convention centre) — present but hidden by default ──
    [MAP]: {
      type: { resolvedName: "Map" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distContentMap: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: false,
        isVisibleHeadline: false, textHeadline: td("smtMapHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("smtMapHeader"), fontSizeHeader: "text-2xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("smtMapText"), fontSizeText: "text-md", textAlignText: "left",
        buttonSize: "medium", buttonText: td("smtMapButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false, buttonAlign: "left",
        mapRounded: "rounded-none",
        mapZoom: 13, mapType: "m", aspectRatio: "aspect-[4/3]", isVisibleMap: true, mapLocation: td("smtMapLocation"),
      },
      displayName: "Map", custom: { icon: "IconMap2", preview: pv("Map-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "smt_mp_eb", header: "smt_mp_hd", text: "smt_mp_tx", button: "smt_mp_btn" },
    },
    smt_mp_eb: tc(MAP, { isVisible: false, text: td("smtMapHeader"), fontSize: "text-lg", textAlign: "left", displayName: "headline", header: true }),
    smt_mp_hd: tc(MAP, { isVisible: true, text: td("smtMapHeader"), fontSize: "text-2xl", textAlign: "left", displayName: "header", header: true }),
    smt_mp_tx: tc(MAP, { isVisible: true, text: td("smtMapText"), fontSize: "text-md", textAlign: "left", displayName: "text", header: false }),
    smt_mp_btn: btn(MAP, { text: td("smtMapButton"), displayName: "button" }),

    // ── Contact (programme office) ──
    [CONTACT]: {
      type: { resolvedName: "Contact" }, isCanvas: true,
      props: {
        sectionHeight: 70, contentAlignment: "center", infoSection: true, backgroundColor: "transparent",
        padding: PAD, sectionMargin: SEC_MARGIN, textSpacing: "space-y-3 wb-md:space-y-5 wb-lg:space-y-6", isVisible: true,
        infoWidth: "50%", inversed: false,
        isVisibleHeadline: true, textHeadline: td("smtContactEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("smtContactHeader"), fontSizeHeader: "text-2xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("smtContactText"), fontSizeText: "text-md", textAlignText: "left",
        isSocialVisible: true, socialIcons: [{ platform: "linkedin", url: "https://linkedin.com" }], socialSize: 24, socialPosition: "left",
        buttonSize: "medium", buttonText: td("smtContactButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false,
      },
      displayName: "Contact", custom: { icon: "IconForms", preview: pv("Contact-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: {
        headline: "smt_ct_eb", header: "smt_ct_hd", text: "smt_ct_tx",
        firstName: "smt_ct_first", lastName: "smt_ct_last", eMail: "smt_ct_email",
        phoneNumber: "smt_ct_phone", Message: "smt_ct_msg", button: "smt_ct_btn",
      },
    },
    smt_ct_eb: tc(CONTACT, { isVisible: true, text: td("smtContactEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: TEAL, header: true }),
    smt_ct_hd: tc(CONTACT, { isVisible: true, text: td("smtContactHeader"), fontSize: "text-2xl", textAlign: "left", displayName: "header", header: true }),
    smt_ct_tx: tc(CONTACT, { isVisible: true, text: td("smtContactText"), fontSize: "text-md", textAlign: "left", displayName: "text", header: false }),
    smt_ct_first: fi(CONTACT, { label: td("firstName"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "firstName", changeable: false }),
    smt_ct_last: fi(CONTACT, { label: td("lastName"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "lastName", changeable: false }),
    smt_ct_email: fi(CONTACT, { label: td("email"), overwriteType: "EMAIL", requiredField: true, inputFieldVariable: "email", changeable: false, locked: true }),
    smt_ct_phone: fi(CONTACT, { label: td("phoneNumber"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "phone", changeable: false }),
    smt_ct_msg: fi(CONTACT, { label: td("messageLabel"), overwriteType: "STRING", requiredField: true, inputFieldVariable: "message", multiline: true, gridArea: "col-span-12", changeable: false, locked: true }),
    smt_ct_btn: btn(CONTACT, { linkType: "internal", linkUrl: "", text: td("smtContactButton"), widthMode: "full", buttonType: "submit", displayName: "button", fixedNavigation: false }),
  });
};

// ── Meta sections (AppBar + Footer) ───────────────────────────────────

/** Paper bar, ink hairline, logo + anchor nav + ink "Request a seat" CTA. */
export const summitAppbar = (td: TranslationFn): string =>
  enc({
    type: { resolvedName: "AppBarSection" }, isCanvas: false,
    props: {
      padding: "p-4 wb-md:p-5 wb-lg:p-6", isVisible: true,
      backgroundColor: PAPER,
      border: true, borderColor: INK, borderWidth: "border", borderStyle: "border-solid", borderSides: ["bottom"],
      sectionOrder: ["logo", "links", "social"],
      isLogoVisible: true, logoSrc: LOGO, logoWidth: "w-32", logoHeight: "h-10", logoPosition: "left",
      isLinksVisible: true, linksPosition: "center",
      links: [
        { label: td("smtLinkProgram"), url: "#smt_agenda", linkType: "internal" },
        { label: td("smtLinkSpeakers"), url: "#smt_speaker", linkType: "internal" },
        { label: td("smtLinkFaq"), url: "#smt_faq", linkType: "internal" },
        { label: td("smtLinkContact"), url: "#smt_contact", linkType: "internal" },
      ],
      linksColor: INK, linksFontSize: "text-sm", linksFontWeight: "font-semibold",
      isSocialVisible: false, socialIcons: [], socialPosition: "right", socialSize: 24,
      logoutButtonSize: "small", logoutButtonText: td("smtHeroPrimaryButton"),
      logoutButtonBorder: true, logoutButtonBackgroundColor: INK, logoutButtonColor: "#FFFFFF", logoutButtonBorderColor: INK,
    },
    displayName: "AppBar", custom: { icon: "IconLayoutNavbar" },
    parent: "DCH9WIIT55", hidden: false, nodes: [], linkedNodes: {},
  });

/** Ink footer, teal top rule, three link groups + social + copyright. */
export const summitFooter = (td: TranslationFn): string =>
  enc({
    type: { resolvedName: "FooterSection" }, isCanvas: false,
    props: {
      padding: "px-4 py-10 wb-md:px-6 wb-lg:px-8", isVisible: true,
      backgroundColor: INK,
      border: true, borderColor: TEAL, borderWidth: "border-4", borderStyle: "border-solid", borderSides: ["top"],
      sectionOrder: ["links", "social"],
      isLogoVisible: false, logoSrc: LOGO, logoWidth: "w-32", logoHeight: "h-auto",
      isLinkGroupsVisible: true,
      linkGroups: [
        { title: td("smtFooterSummit"), links: [
          { label: td("smtLinkProgram"), url: "#smt_agenda", linkType: "internal" },
          { label: td("smtLinkSpeakers"), url: "#smt_speaker", linkType: "internal" },
          { label: td("smtLinkRsvp"), url: "/register", linkType: "internal" },
        ] },
        // Event practicalities — Maps + add-to-calendar are EXTERNAL (open in
        // a new tab). URLs are placeholders the organiser edits per event.
        { title: td("smtFooterEvent"), links: [
          { label: td("smtLinkLocation"), url: "https://www.google.com/maps/search/?api=1&query=Frankfurt", linkType: "external" },
          { label: td("smtLinkCalendar"), url: "https://calendar.google.com/calendar/render?action=TEMPLATE&text=Executive+Summit", linkType: "external" },
          { label: td("smtLinkContact"), url: "#smt_contact", linkType: "internal" },
        ] },
        { title: td("smtFooterLegal"), links: [
          { label: td("smtLinkPrivacy"), url: "/privacy", linkType: "internal" },
          { label: td("smtLinkImprint"), url: "/imprint", linkType: "internal" },
          { label: td("smtLinkTerms"), url: "/terms", linkType: "internal" },
        ] },
      ],
      linksTitleColor: "#FFFFFF", linksTitleFontSize: "text-sm", linksTitleFontWeight: "font-semibold",
      linksColor: "#B9B3A8", linksFontSize: "text-sm", linksFontWeight: "font-normal", linksHoverColor: "hover:font-primary",
      isSocialVisible: true,
      socialIcons: [
        { altText: "linkedin", platform: "linkedin", url: "https://linkedin.com" },
      ],
      socialColor: "#B9B3A8", socialHoverColor: "hover:font-primary", socialSize: 20, socialTitle: td("followUs"),
      isCopyrightVisible: true, copyrightColor: "#8A857B", copyrightFontSize: "text-xs", copyrightCompanyName: td("yourCompany"),
    },
    displayName: "Footer", custom: { icon: "IconMenu2" },
    parent: "xaUW2UDZT8", hidden: false, nodes: [], linkedNodes: {},
  });
